import { useDispatch } from "react-redux";
import { Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";

import { BsShopWindow } from "react-icons/bs";
import { CgNotes } from "react-icons/cg";
import { RiDeleteBin2Line } from "react-icons/ri";
import { GiCash } from "react-icons/gi";
import { FaUser } from "react-icons/fa";
import { BiWorld } from "react-icons/bi";
import { HiOutlineMail } from "react-icons/hi";
import { ImPhone } from "react-icons/im";

import useTheme from "hooks/useTheme";
import useNotification from "hooks/useNotification";

import DeleteModal from "theme/modals/partials/DeleteByPinModal";
import { convertBase64 } from "helpers/functions";
import { deleteContactById, updateContactInfoByID } from "actions/contacts";
import ReportsIcon from "theme/icons/Reports";
import AlgoExplorerIcon from "theme/icons/AlgoExplorer";
import CopyIcon from "theme/icons/Copy";
import UserIcon from "theme/icons/UserIcon";
import EditIcon from "theme/icons/Edit";
import StyledTooltipWithoutArrow from "theme/tooltips/StyledTooltipWithoutArrow";
import ComingSoonModal from "theme/modals/partials/ComingSoonModal";

const ContactInfo = ({ contactInfo, setActiveContact }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const hiddenImageFileInput = useRef();

	const [comingSoonModalIsOpen, setComingSoonModalIsOpen] = useState(false);
	const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
	const [tempContactInfo, setTempContactInfo] = useState(null);
	const [isDataUpdated, setIsDataUpdated] = useState(false);

	useEffect(() => {
		setTempContactInfo(contactInfo);
		setDeleteModalIsOpen(false);
		setIsDataUpdated(false);
	}, [contactInfo]);

	useEffect(() => {
		if (isDataUpdated) handleSubmit();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDataUpdated]);

	const handleFileRead = async (e) => {
		const file = e.target.files[0];
		const base64 = await convertBase64(file);

		setTempContactInfo({ ...tempContactInfo, "profile-image": base64 });

		setIsDataUpdated(true);
	};

	const handleChange = (e) => {
		setTempContactInfo({
			...tempContactInfo,
			[e.target.name]: e.target.valueAsNumber || e.target.value,
		});

		setIsDataUpdated(true);
	};
	const handleSubmit = () => {
		dispatch(updateContactInfoByID(tempContactInfo, notify));

		setIsDataUpdated(false);
	};

	return (
		tempContactInfo && (
			<Transition
				as={Fragment}
				appear={true}
				show={true}
				enter="transition-opacity duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
			>
				<div className="flex flex-col w-[50%] items-center justify-start px-5 space-y-2">
					{/* <div className={`flex items-center w-full border-b-2 pb-1 ${themeColor?.border}`}>
						<h1 className={`w-full text-xl font-bold ${themeColor?.text}`}>Contact Details</h1>
						{isDataUpdated && (
							<button
								className={`text-md bg-candy-red text-white px-3 py-1 rounded-lg`}
								onClick={() => handleSubmit()}
							>
								Save
							</button>
						)}
					</div> */}

					<div className="flex-grow flex-col w-full overflow-y-auto space-y-7">
						<div className="flex w-full items-start">
							<div className="flex-col relative">
								{tempContactInfo && tempContactInfo["profile-image"] ? (
									<img
										className={`w-14 h-14 object-contain object-center`}
										src={tempContactInfo["profile-image"]}
										alt="temp"
										loading="lazy"
									/>
								) : (
									<UserIcon className="w-14 h-14" fill={themeColor?.hex} />
								)}
								<EditIcon
									className="w-5 absolute -bottom-1 -right-1 cursor-pointer bg-white rounded-md hover:shadow-lg"
									fill={themeColor?.hex}
									onClick={() => hiddenImageFileInput.current.click()}
								/>
								<input
									style={{ display: "none" }}
									ref={hiddenImageFileInput}
									type="file"
									accept="image/x-png,image/jpeg"
									onChange={(e) => handleFileRead(e)}
								/>
							</div>
							<div className="flex-col w-full space-y-4 pl-5">
								<div className="flex items-start justify-start">
									<div className="flex-col">
										<h1 className={`text-md font-bold ${themeColor?.text}`}>
											{tempContactInfo["friendly-name"]}
										</h1>
										<div className="flex space-x-3">
											<h2 className={`flex break-all text-xs font-medium ${themeColor?.text}`}>
												{tempContactInfo["account-address"].slice(0, 5)}...
												{tempContactInfo["account-address"].slice(-5)}
											</h2>
											<CopyIcon
												className={`w-4 cursor-pointer`}
												fill={themeColor?.hex}
												onClick={() => {
													navigator.clipboard.writeText(tempContactInfo["account-address"]);
													notify("success", "Copied to clipboard");
												}}
											/>
										</div>
									</div>
								</div>
								<div className="w-full flex space-x-2 items-center justify-between">
									<div className="flex space-x-2">
										<ReportsIcon
											className="w-4 cursor-pointer"
											fill={themeColor?.hex}
											onClick={() => setComingSoonModalIsOpen(true)}
										/>
										<StyledTooltipWithoutArrow
											title={appLanguage?.algoExplorer}
											children={
												<AlgoExplorerIcon
													className={`w-4 cursor-pointer`}
													fill={themeColor?.hex}
													onClick={() =>
														window.open(
															`https://algoexplorer.io/address/${tempContactInfo["account-address"]}`,
															"_blank"
														)
													}
												/>
											}
										/>
										{/* <ReportsIcon className="w-6" fill={themeColor?.hex} />
										<AlgoExplorerIcon className="w-6" fill={themeColor?.hex} /> */}
									</div>
								</div>
							</div>
						</div>

						<div className="flex-col w-full space-y-3">
							<div className="flex w-full space-x-3 items-center">
								<FaUser className={`text-2xl text-bold ${themeColor?.text}`} />

								<div className="w-full grid grid-cols-2 gap-2">
									<div className="col-span1">
										<input
											className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
											placeholder={appLanguage?.firstName}
											name="first-name"
											value={tempContactInfo["first-name"] ? tempContactInfo["first-name"] : ""}
											onChange={(e) => handleChange(e)}
										/>
									</div>
									<div className="col-span1">
										<input
											className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
											placeholder={appLanguage?.lastName}
											name="last-name"
											value={tempContactInfo["last-name"] ? tempContactInfo["last-name"] : ""}
											onChange={(e) => handleChange(e)}
										/>
									</div>
								</div>
							</div>

							<div className="flex w-full space-x-3 items-center">
								<GiCash className={`text-2xl text-bold text-white`} />
								<input
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.friendlyName}
									name="friendly-name"
									value={tempContactInfo["friendly-name"] ? tempContactInfo["friendly-name"] : ""}
									onChange={(e) => handleChange(e)}
								/>
							</div>

							<div className="flex w-full space-x-3 items-center">
								<GiCash className={`text-2xl text-bold ${themeColor?.text}`} />
								<input
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.accountAddress}
									// name="account-address"
									value={tempContactInfo["account-address"] ? tempContactInfo["account-address"] : ""}
									// onChange={(e) => handleChange(e)}
									disabled
								/>
							</div>
							<div className="flex w-full space-x-3 items-center">
								<BsShopWindow className={`text-2xl text-bold ${themeColor?.text}`} />
								<input
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.company}
									name="company"
									value={tempContactInfo?.company ? tempContactInfo.company : ""}
									onChange={(e) => handleChange(e)}
								/>
							</div>
							<div className="flex w-full space-x-3 items-center">
								<ImPhone className={`text-2xl text-bold ${themeColor?.text}`} />
								<input
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.phoneNumber}
									name="phone"
									value={tempContactInfo?.phone ? tempContactInfo.phone : ""}
									onChange={(e) => handleChange(e)}
								/>
							</div>
							<div className="flex w-full space-x-3 items-center">
								<HiOutlineMail className={`text-2xl text-bold ${themeColor?.text}`} />
								<input
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.email}
									name="email"
									value={tempContactInfo?.email ? tempContactInfo.email : ""}
									onChange={(e) => handleChange(e)}
								/>
							</div>
							<div className="flex w-full space-x-3 items-start">
								<BiWorld className={`text-2xl text-bold ${themeColor?.text}`} />
								<textarea
									rows="4"
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.streetAddress}
									name="street-address"
									value={tempContactInfo["street-address"] ? tempContactInfo["street-address"] : ""}
									onChange={(e) => handleChange(e)}
								></textarea>
							</div>
							<div className="flex w-full space-x-3 items-start">
								<CgNotes className={`text-2xl text-bold ${themeColor?.text}`} />
								<textarea
									rows="2"
									className={`w-full italic text-xs border rounded-3xl px-3 py-2 focus:outline-none`}
									placeholder={appLanguage?.notes}
									name="notes"
									value={tempContactInfo?.notes ? tempContactInfo.notes : ""}
									onChange={(e) => handleChange(e)}
								></textarea>
							</div>
						</div>

						{/* <div className="flex-col w-full space-y-2">
							<div className="flex w-full space-x-20 items-center justify-between">
								<p className={`text-xs ${themeColor?.text}`}>Creation {appLanguage?.creationDate}
								<p className={`text-xs ${themeColor?.text}`}>
									{tempContactInfo["created-at"]
										? format(parseISO(tempContactInfo["created-at"]), "MMM dd, yyyy")
										: "-"}
								</p>
							</div>
							<div className="flex w-full space-x-20 items-center justify-between">
								<p className={`text-xs ${themeColor?.text}`}>Last Used {appLanguage?.lastUsedDate}
								<p className={`text-xs ${themeColor?.text}`}>
									{tempContactInfo["updated-at"]
										? format(parseISO(tempContactInfo["updated-at"]), "MMM dd, yyyy")
										: "-"}
								</p>
							</div>
						</div> */}

						<div className="flex w-full items-center justify-center pb-5">
							<RiDeleteBin2Line
								className={`text-2xl text-bold text-candy-red cursor-pointer`}
								onClick={() => setDeleteModalIsOpen(true)}
							/>
						</div>
					</div>

					<ComingSoonModal isOpen={comingSoonModalIsOpen} setIsOpen={setComingSoonModalIsOpen} />
					<DeleteModal
						isOpen={deleteModalIsOpen}
						setIsOpen={setDeleteModalIsOpen}
						title={tempContactInfo["friendly-name"]}
						action={deleteContactById({ _id: tempContactInfo?._id }, notify)}
						setActive={setActiveContact}
					/>
				</div>
			</Transition>
		)
	);
};

export default ContactInfo;
